import ServiceWorksPlanning from "components/modal/serviceWorksPlanning";
import { ServiceWorksTooltipContent } from "components/planning/serviceWorksTooltipContent";
import { dateTimeToView } from "helpers/date";
import { carPlanningStatuses, carPlanningStatusesEnum } from "helpers/enums";
import { enumLabel, formatPhone, ymEvent } from "helpers/system";
import { fullName, lastNameWithInitials } from "helpers/user";
import Phone from "icons/planning/phone.svg";
import ShiftChange from "icons/planning/shift-change.svg";
import Wrench from "icons/planning/wrench.svg";
import Tooltip from "rc-tooltip";
import React, { useMemo } from "react";

export default function Car(props) {
    const { serviceWorks, car, trailer, driverShift, driver, carPlanningStatus } = props.data;
    const [cname, tooltipData] = useMemo(() => {
        switch (carPlanningStatus) {
            case carPlanningStatusesEnum.HasPlannedBid:
                return [
                    "p-car p-car--planned",
                    {
                        overlayClassName: "p-car__tooltip p-car__tooltip--planned",
                        overlay: enumLabel(carPlanningStatuses, carPlanningStatus),
                    },
                ];
            case carPlanningStatusesEnum.HasNoPlannedBid:
                return [
                    "p-car p-car--not-planned",
                    {
                        overlayClassName: "p-car__tooltip p-car__tooltip--not-planned",
                        overlay: enumLabel(carPlanningStatuses, carPlanningStatus),
                    },
                ];
            case carPlanningStatusesEnum.AtUnloading:
                return [
                    "p-car p-car--unloading",
                    {
                        overlayClassName: "p-car__tooltip p-car__tooltip--unloading",
                        overlay: enumLabel(carPlanningStatuses, carPlanningStatus),
                    },
                ];
            case carPlanningStatusesEnum.NoActiveBid:
                return [
                    "p-car p-car--without-bid",
                    {
                        overlayClassName: "p-car__tooltip p-car__tooltip--without-bid",
                        overlay: enumLabel(carPlanningStatuses, carPlanningStatus),
                    },
                ];
            case carPlanningStatusesEnum.Booked:
                return [
                    "p-car p-car--booked",
                    {
                        overlayClassName: "p-car__tooltip p-car__tooltip--booked",
                        overlay: (
                            <>
                                {enumLabel(carPlanningStatuses, carPlanningStatus)}
                                <br />
                                {car.bookingComment}
                            </>
                        ),
                    },
                ];
        }
    }, [carPlanningStatus, car.bookingComment]);

    const driverShiftContent = useMemo(() => {
        if (!driverShift) {
            return null;
        }

        const {
            plannedEndDateOfCurrentShift,
            plannedStartDateOfNextShift,
            address,
            currentDriverFullName,
            nextDriverFullName,
            createdAt,
            updatedAt,
            comment,
        } = driverShift;
        return (
            <>
                <div>
                    <span className="pr-2 font-weight-semibold p-car__yellow-text">Пересменка:</span>
                    <span>{address}</span>
                </div>
                <div>
                    <span className="pr-2 font-weight-semibold p-car__yellow-text">
                        Плановая дата окончания последней смены:
                    </span>
                    <span>{dateTimeToView(plannedEndDateOfCurrentShift, "DD.MM.YYYY HH:mm (Z)")}</span>
                </div>
                <div>
                    <span className="pr-2 font-weight-semibold p-car__yellow-text">Плановая дата начала смены:</span>
                    <span>{dateTimeToView(plannedStartDateOfNextShift, "DD.MM.YYYY HH:mm (Z)")}</span>
                </div>
                <div>
                    <span className="pr-2 font-weight-semibold p-car__yellow-text">ФИО водителя последней смены:</span>
                    <span>{currentDriverFullName || "Не назначен"}</span>
                </div>
                <div>
                    <span className="pr-2 font-weight-semibold p-car__yellow-text">ФИО водителя новой смены:</span>
                    <span>{nextDriverFullName || "Не назначен"}</span>
                </div>
                <div>
                    <span className="pr-2 font-weight-semibold p-car__yellow-text">Дата создания пересменки:</span>
                    <span>{dateTimeToView(createdAt, "DD.MM.YYYY HH:mm (Z)")}</span>
                </div>
                <div>
                    <span className="pr-2 font-weight-semibold p-car__yellow-text">
                        Последняя дата редактирования пересменки:
                    </span>
                    <span>{dateTimeToView(updatedAt, "DD.MM.YYYY HH:mm (Z)")}</span>
                </div>
                {comment && (
                    <div>
                        <span className="pr-2 font-weight-semibold p-car__yellow-text">Комментарий:</span>
                        <span>{comment}</span>
                    </div>
                )}
            </>
        );
    }, [driverShift]);

    const serviceWorksContent = useMemo(() => {
        if (!serviceWorks?.length) {
            return null;
        }
        return <ServiceWorksTooltipContent serviceWorks={serviceWorks} />;
    }, [serviceWorks]);

    const driverOverlay = useMemo(() => {
        return driver ? (
            <>
                <div>{fullName(driver)}</div>
                {driver.phoneNumber && (
                    <div>
                        Тел: <span className="font-weight-semibold">{formatPhone(driver.phoneNumber)}</span>
                    </div>
                )}
            </>
        ) : (
            ""
        );
    }, [driver]);

    return (
        <div className="w-100">
            <div className="d-inline-flex w-100">
                <div className="p-car__wrap">
                    <Tooltip {...tooltipData}>
                        <div className={cname} data-test-id="trip-planning-car-planning-status">
                            <span data-test-id="trip-planning-car-number">{car.number}</span>
                            {trailer?.number && (
                                <>
                                    {" - "}
                                    <span data-test-id="trip-planning-trailer-number">{trailer.number}</span>
                                </>
                            )}
                        </div>
                    </Tooltip>
                </div>
                <div className="text-nowrap">
                    {serviceWorks?.length > 0 && (
                        <Tooltip
                            destroyTooltipOnHide
                            overlayClassName="planning-table__body--tooltip-scroll"
                            placement="bottom"
                            overlay={serviceWorksContent}
                        >
                            <Wrench
                                className="p-table__icon-fill cursor-pointer"
                                data-test-id="trip-planning-service-works"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    props.modal({
                                        name: "default",
                                        title: "Сервисные работы",
                                        windowMod: "wide",
                                        CustomBody: ServiceWorksPlanning,
                                        bodyData: { serviceWorks },
                                    });
                                }}
                            />
                        </Tooltip>
                    )}
                    {driverShift && (
                        <Tooltip
                            overlayClassName="planning-table__body--tooltip"
                            placement="bottom"
                            overlay={driverShiftContent}
                        >
                            <ShiftChange className="p-table__icon-fill" data-test-id="trip-planning-driver-shift" />
                        </Tooltip>
                    )}
                </div>
            </div>
            {driver && (
                <Tooltip overlay={driverOverlay}>
                    <div className="p-car__driver">
                        <span className="text-muted" data-test-id="trip-planning-driver-name">
                            {lastNameWithInitials(driver)}
                        </span>
                        {driver?.phoneNumber && (
                            <a
                                className="ml-2"
                                data-test-id="trip-planning-driver-phone"
                                href={`callto:${driver.phoneNumber}`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    ymEvent("TripPlanningContactPhoneClicked");
                                }}
                            >
                                <Phone className="p-cell--text-success" />
                            </a>
                        )}
                    </div>
                </Tooltip>
            )}
        </div>
    );
}
